import { formatDate } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import {
  PageHeaderWrapper,
  Text,
  TextSkeleton,
  useModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useCallback } from "react";

import { IconButton } from "../../../components/IconButton";
import { EarningsPageOptionsBottomSheet } from "./EarningsPageOptionsBottomSheet";

export interface EarningsPageHeaderProps {
  firstPaymentCreatedAt: string | undefined;
  totalEarningsInCents: number | undefined;
  isLoading: boolean;
  isGoToStripeLoading: boolean;
  onClickBack: () => void;
  onGoToStripe: () => void;
  onGoToProofOfEarnings: () => void;
  onViewTaxInfo: () => void;
  onGoToPayoutSettings: () => void;
}

export function EarningsPageHeader(props: EarningsPageHeaderProps) {
  const {
    firstPaymentCreatedAt,
    totalEarningsInCents,
    isLoading,
    isGoToStripeLoading,
    onClickBack,
    onGoToStripe,
    onGoToProofOfEarnings,
    onViewTaxInfo,
    onGoToPayoutSettings,
  } = props;
  const optionsBottomSheetModalState = useModalState();

  // show expanded header area (and gradient) if:
  // - we're still loading
  // OR
  // - we have the data (first shift date + total earnings)
  const shouldShowLifetimeEarningsSection =
    isLoading || (isDefined(firstPaymentCreatedAt) && isDefined(totalEarningsInCents));

  const onSettingsClick = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.EARNINGS_PAGE_SETTINGS_SHOWN);
    optionsBottomSheetModalState.openModal();
  }, [optionsBottomSheetModalState]);

  return (
    <>
      <PageHeaderWrapper
        sx={{
          background: (theme) =>
            shouldShowLifetimeEarningsSection
              ? theme.palette.intent?.success.gradient
              : theme.palette.background.tertiary,
          borderRadius: 0,
        }}
      >
        <Stack
          spacing={8}
          sx={(theme) => ({
            paddingTop: theme.spacing(3),
            paddingBottom: theme.spacing(5),
            paddingX: theme.spacing(5),
          })}
        >
          <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
            <IconButton
              title="Go Back"
              iconType="chevron-left"
              variant="outlined"
              onClick={onClickBack}
            />
            {shouldShowLifetimeEarningsSection ? (
              // if we're not showing the lifetime earnings, there's not point in showing settings either
              // everything that's in settings should be surfaced to the empty state of the UI!
              <IconButton
                title="Settings"
                iconType="ellipsis"
                variant="outlined"
                onClick={onSettingsClick}
              />
            ) : null}
          </Stack>
          {shouldShowLifetimeEarningsSection ? (
            <Stack spacing={1}>
              {isDefined(firstPaymentCreatedAt) ? (
                <Text semibold variant="body1">
                  Clipboard earnings since {formatDate(firstPaymentCreatedAt)}:
                </Text>
              ) : (
                <TextSkeleton variant="body1" width="200px" />
              )}
              {isDefined(totalEarningsInCents) ? (
                <Text variant="h2">{formatDollarsAsUsd(totalEarningsInCents / 100)}</Text>
              ) : (
                <TextSkeleton variant="h2" width="150px" />
              )}
            </Stack>
          ) : null}
        </Stack>
      </PageHeaderWrapper>
      <EarningsPageOptionsBottomSheet
        modalState={optionsBottomSheetModalState}
        isGoToStripeLoading={isGoToStripeLoading}
        onGoToStripe={onGoToStripe}
        onGoToProofOfEarnings={onGoToProofOfEarnings}
        onViewTaxInfo={onViewTaxInfo}
        onGoToPayoutSettings={onGoToPayoutSettings}
      />
    </>
  );
}
